/**
 * 间距 Design Token 系统
 * 基于 8px 网格系统的完整间距规范
 */

import type { SpacingTokens } from './types';

// 间距 Token 定义
export const spacingTokens: SpacingTokens = {
  // 基础间距 (基于 8px 网格系统)
  base: {
    0: 0,       // 0px - 无间距
    1: 4,       // 0.25rem - 极小间距
    2: 8,       // 0.5rem - 小间距 (基准单位)
    3: 12,      // 0.75rem - 
    4: 16,      // 1rem - 标准间距
    5: 20,      // 1.25rem - 
    6: 24,      // 1.5rem - 中等间距
    8: 32,      // 2rem - 大间距
    10: 40,     // 2.5rem - 
    12: 48,     // 3rem - 很大间距
    16: 64,     // 4rem - 超大间距
    20: 80,     // 5rem - 
    24: 96,     // 6rem - 极大间距
    32: 128,    // 8rem - 特大间距
  },
  
  // 组件间距
  component: {
    // 内边距
    paddingXs: 8,       // 0.5rem - 极小内边距
    paddingSm: 12,      // 0.75rem - 小内边距
    paddingMd: 16,      // 1rem - 标准内边距
    paddingLg: 24,      // 1.5rem - 大内边距
    paddingXl: 32,      // 2rem - 超大内边距
    
    // 外边距
    marginXs: 8,        // 0.5rem - 极小外边距
    marginSm: 12,       // 0.75rem - 小外边距
    marginMd: 16,       // 1rem - 标准外边距
    marginLg: 24,       // 1.5rem - 大外边距
    marginXl: 32,       // 2rem - 超大外边距
    
    // 组件间距
    gapXs: 8,           // 0.5rem - 极小间隙
    gapSm: 12,          // 0.75rem - 小间隙
    gapMd: 16,          // 1rem - 标准间隙
    gapLg: 24,          // 1.5rem - 大间隙
    gapXl: 32,          // 2rem - 超大间隙
  },
  
  // 布局间距
  layout: {
    // 页面级间距
    pageTop: 24,        // 1.5rem - 页面顶部间距
    pageBottom: 48,     // 3rem - 页面底部间距
    pageHorizontal: 24, // 1.5rem - 页面水平间距
    
    // 区块间距
    sectionVertical: 64,    // 4rem - 区块垂直间距
    sectionHorizontal: 32,  // 2rem - 区块水平间距
    
    // 容器间距
    containerPadding: 24,   // 1.5rem - 容器内边距
    containerMargin: 16,    // 1rem - 容器外边距
    
    // 卡片间距
    cardPadding: 24,        // 1.5rem - 卡片内边距
    cardMargin: 16,         // 1rem - 卡片外边距
    cardGap: 24,            // 1.5rem - 卡片间隙
    
    // 表单间距
    formItemMargin: 16,     // 1rem - 表单项外边距
    formGroupMargin: 24,    // 1.5rem - 表单组外边距
    formSectionMargin: 32,  // 2rem - 表单区块外边距
  },
};

// 响应式间距系统
export const responsiveSpacing = {
  // 移动端间距调整
  mobile: {
    base: spacingTokens.base, // 基础间距保持不变
    component: {
      ...spacingTokens.component,
      paddingMd: 12,      // 减小标准内边距
      paddingLg: 16,      // 减小大内边距
      marginMd: 12,       // 减小标准外边距
      marginLg: 16,       // 减小大外边距
    },
    layout: {
      pageTop: 16,            // 减小页面顶部间距
      pageBottom: 32,         // 减小页面底部间距
      pageHorizontal: 16,     // 减小页面水平间距
      sectionVertical: 40,    // 减小区块垂直间距
      sectionHorizontal: 16,  // 减小区块水平间距
      containerPadding: 16,   // 减小容器内边距
      containerMargin: 12,    // 减小容器外边距
      cardPadding: 16,        // 减小卡片内边距
      cardMargin: 12,         // 减小卡片外边距
      cardGap: 16,            // 减小卡片间隙
      formItemMargin: 12,     // 减小表单项外边距
      formGroupMargin: 16,    // 减小表单组外边距
      formSectionMargin: 24,  // 减小表单区块外边距
    },
  },
  
  // 平板端间距调整
  tablet: {
    base: spacingTokens.base,
    component: {
      ...spacingTokens.component,
      paddingLg: 20,      // 略微减小大内边距
      marginLg: 20,       // 略微减小大外边距
    },
    layout: {
      pageTop: 20,            // 略微减小页面顶部间距
      pageBottom: 40,         // 略微减小页面底部间距
      pageHorizontal: 20,     // 略微减小页面水平间距
      sectionVertical: 56,    // 略微减小区块垂直间距
      sectionHorizontal: 24,  // 保持区块水平间距
      containerPadding: 20,   // 略微减小容器内边距
      containerMargin: 16,    // 保持容器外边距
      cardPadding: 20,        // 略微减小卡片内边距
      cardMargin: 16,         // 保持卡片外边距
      cardGap: 20,            // 略微减小卡片间隙
      formItemMargin: 16,     // 保持表单项外边距
      formGroupMargin: 20,    // 略微减小表单组外边距
      formSectionMargin: 28,  // 略微减小表单区块外边距
    },
  },
  
  // 桌面端间距 (默认)
  desktop: spacingTokens,
};

// 间距使用规范
export const spacingUsage = {
  // 基础间距应用场景
  baseUsage: {
    0: '重置间距、紧密布局',
    1: '极小装饰间距、图标与文字间距',
    2: '小间距、按钮内边距、标签间距',
    3: '中小间距、表单元素间距',
    4: '标准间距、列表项间距、卡片内边距',
    5: '中等间距、组件间距',
    6: '大间距、区块内间距',
    8: '很大间距、页面区块间距',
    10: '超大间距、页面级间距',
    12: '极大间距、主要区块间距',
    16: '特大间距、页面顶部/底部间距',
    20: '超特大间距、特殊布局间距',
    24: '最大间距、页面级大间距',
    32: '超级间距、特殊页面间距',
  },
  
  // 组件间距应用场景
  componentUsage: {
    padding: {
      xs: '按钮、标签、徽章内边距',
      sm: '输入框、小卡片内边距',
      md: '标准卡片、表单项内边距',
      lg: '大卡片、容器内边距',
      xl: '页面容器、主要区块内边距',
    },
    margin: {
      xs: '小元素外边距、图标间距',
      sm: '表单元素外边距、列表项间距',
      md: '卡片外边距、组件间距',
      lg: '区块外边距、页面组件间距',
      xl: '主要区块外边距、页面级间距',
    },
    gap: {
      xs: '紧密网格间距、标签组间距',
      sm: '标准网格间距、按钮组间距',
      md: '卡片网格间距、表单组间距',
      lg: '大网格间距、页面组件间距',
      xl: '超大网格间距、主要区块间距',
    },
  },
};

// 导出间距常量，用于 SCSS 变量兼容
export const spacingConstants = {
  // 基础间距
  SPACING_0: spacingTokens.base[0],
  SPACING_1: spacingTokens.base[1],
  SPACING_2: spacingTokens.base[2],
  SPACING_3: spacingTokens.base[3],
  SPACING_4: spacingTokens.base[4],
  SPACING_5: spacingTokens.base[5],
  SPACING_6: spacingTokens.base[6],
  SPACING_8: spacingTokens.base[8],
  SPACING_10: spacingTokens.base[10],
  SPACING_12: spacingTokens.base[12],
  SPACING_16: spacingTokens.base[16],
  SPACING_20: spacingTokens.base[20],
  SPACING_24: spacingTokens.base[24],
  SPACING_32: spacingTokens.base[32],
  
  // 组件间距
  PADDING_XS: spacingTokens.component.paddingXs,
  PADDING_SM: spacingTokens.component.paddingSm,
  PADDING_MD: spacingTokens.component.paddingMd,
  PADDING_LG: spacingTokens.component.paddingLg,
  PADDING_XL: spacingTokens.component.paddingXl,
  
  MARGIN_XS: spacingTokens.component.marginXs,
  MARGIN_SM: spacingTokens.component.marginSm,
  MARGIN_MD: spacingTokens.component.marginMd,
  MARGIN_LG: spacingTokens.component.marginLg,
  MARGIN_XL: spacingTokens.component.marginXl,
  
  GAP_XS: spacingTokens.component.gapXs,
  GAP_SM: spacingTokens.component.gapSm,
  GAP_MD: spacingTokens.component.gapMd,
  GAP_LG: spacingTokens.component.gapLg,
  GAP_XL: spacingTokens.component.gapXl,
  
  // 布局间距
  PAGE_TOP: spacingTokens.layout.pageTop,
  PAGE_BOTTOM: spacingTokens.layout.pageBottom,
  PAGE_HORIZONTAL: spacingTokens.layout.pageHorizontal,
  SECTION_VERTICAL: spacingTokens.layout.sectionVertical,
  SECTION_HORIZONTAL: spacingTokens.layout.sectionHorizontal,
  CONTAINER_PADDING: spacingTokens.layout.containerPadding,
  CONTAINER_MARGIN: spacingTokens.layout.containerMargin,
  CARD_PADDING: spacingTokens.layout.cardPadding,
  CARD_MARGIN: spacingTokens.layout.cardMargin,
  CARD_GAP: spacingTokens.layout.cardGap,
} as const;